/**
 * Map bounds + coordinate helpers.
 *
 * Ported verbatim from the Admin app's
 * `sw-ev-admin/src/components/ChargerNetworkMap/mapStatus.js` so both
 * apps compute the same map viewport for the same charger set. The
 * repos are separate deployables (no monorepo tooling), so the only
 * way to keep behaviour aligned is to share these pure helpers by
 * copy — keep this file in sync with the Admin version when either
 * changes.
 *
 * Admin-specific helpers (statusClass, statusLabel, hasHalo,
 * filterStations, matchesQuery, clampPage, paginate) are intentionally
 * NOT ported — the user app has its own status-classification,
 * search-ranking, and pagination systems and shouldn't duplicate them.
 */

/**
 * Coerce lat/lng to finite numbers, or return null. Guards against
 * NaN, Infinity, and the "null island" (0, 0) sentinel that some
 * legacy stations were written with before the coord fields were
 * required.
 */
export function parseLatLng(station) {
  if (!station) return null;
  const lat = Number(station.lat ?? station.latitude);
  const lng = Number(station.lng ?? station.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  return { lat, lng };
}

/**
 * Split a station list into pinned (has coords) and unpinned (missing
 * coords). Callers can render the unpinned list separately without
 * dropping the rows entirely.
 */
export function splitByLocation(stations) {
  const pinned = [];
  const unpinned = [];
  for (const s of stations || []) {
    if (parseLatLng(s)) pinned.push(s);
    else unpinned.push(s);
  }
  return { pinned, unpinned };
}

// Default fallback view (Barbados). Overridable per-deployment via
// VITE_MAP_FALLBACK_* env vars at the render site.
export const FALLBACK_CENTER = { lat: 13.13, lng: -59.6 };
export const FALLBACK_ZOOM = 11;
// When exactly one marker exists, zoom in close but not to street level.
export const SINGLE_MARKER_ZOOM = 13;
// Cap zoom after fitBounds so a tight cluster of pins isn't zoomed in on
// so hard that surrounding context is lost.
export const MAX_FIT_ZOOM = 14;

/**
 * Decide how the map should be positioned given the current set of
 * marker coordinates. Pure — no Google Maps instance required.
 *
 * Returns one of:
 *   { mode: 'fallback', center, zoom }
 *   { mode: 'single',   center, zoom }
 *   { mode: 'fit',      bounds: { north, south, east, west }, maxZoom }
 */
export function computeBoundsStrategy(coords) {
  const valid = (coords || []).filter(
    (c) => c && Number.isFinite(c.lat) && Number.isFinite(c.lng),
  );
  if (valid.length === 0) {
    return { mode: 'fallback', center: FALLBACK_CENTER, zoom: FALLBACK_ZOOM };
  }
  if (valid.length === 1) {
    const [only] = valid;
    return {
      mode: 'single',
      center: { lat: only.lat, lng: only.lng },
      zoom: SINGLE_MARKER_ZOOM,
    };
  }
  let north = -Infinity;
  let south = Infinity;
  let east = -Infinity;
  let west = Infinity;
  for (const c of valid) {
    if (c.lat > north) north = c.lat;
    if (c.lat < south) south = c.lat;
    if (c.lng > east) east = c.lng;
    if (c.lng < west) west = c.lng;
  }
  return {
    mode: 'fit',
    bounds: { north, south, east, west },
    maxZoom: MAX_FIT_ZOOM,
  };
}

/**
 * Stable signature for a set of station coordinates. The map refits
 * only when this signature changes, so an unrelated re-render (status
 * flip, popup toggle) doesn't yank the user out of their chosen view.
 */
export function coordSignature(stations) {
  const parts = [];
  for (const s of stations || []) {
    const coords = parseLatLng(s);
    if (!coords) continue;
    const id = s.stationId || s.station_id || `${coords.lat},${coords.lng}`;
    parts.push(`${id}:${coords.lat.toFixed(5)},${coords.lng.toFixed(5)}`);
  }
  parts.sort();
  return parts.join('|');
}
